/**
 * sdkBiddingA3p.js - per-adapter-class token walk of the GAM `a3p` value. Browser twin of
 * proxy-server/sdkBidding/a3pWalk.js (pinned by sdkBiddingParity.test.js); the protobuf walk
 * itself is utils/a3pDecode.js decodeA3p, unchanged. Token truth: field 4 of the entry whose
 * field 5 is the buyer's adapter class is non-empty. An entry with an empty signal is `empty`,
 * not present (Liftoff on 490 / 1304 / 389).
 *
 * Fallback only: the proxy answers the sign-off; keep this behind the proxy's answer.
 * docs/sdlc/sdk-bidding-signoff/20-requirements.md ACs 11-17, 72. Never throws.
 */

import { decodeA3p } from '../utils/a3pDecode';

// Copy of proxy mediationNetworks.js adapterClasses for the two buyers the token check covers.
// sdkBiddingParity.test.js compares this table to the proxy registry.
export const TOKEN_BUYERS = [
  {
    id: 'meta',
    adapterClasses: [
      'com.google.ads.mediation.facebook.FacebookMediationAdapter',
      'com.google.ads.mediation.facebook.FacebookAdapter',
      'GADMediationAdapterFacebook',
    ],
  },
  {
    id: 'liftoff',
    adapterClasses: [
      'com.google.ads.mediation.vungle.VungleMediationAdapter',
      'GADMediationAdapterVungle',
    ],
  },
];

const TRUNCATED_ISSUE_CODE = 'ingest_url_truncated';

export function walkA3p(raw) {
  const d = decodeA3p(raw);
  const entries = (d.entries || []).map((e) => ({
    adapterClass: e.adapter,
    signalLength: e.signalLength,
    tokenPresent: e.signalLength > 0,
  }));
  return { ok: d.ok, bytes: d.bytes, entries, ...(d.error ? { error: d.error } : {}) };
}

const leaf = (cls) => String(cls || '').split('.').pop();

function entriesForClasses(entries, classes) {
  const exact = entries.filter((e) => classes.includes(e.adapterClass));
  if (exact.length) return exact;
  const leaves = classes.map(leaf);
  return entries.filter((e) => leaves.includes(leaf(e.adapterClass)));
}

export function a3pFromUrl(url) {
  const m = /[?&]a3p=([^&#]*)/.exec(String(url || ''));
  return m ? m[1] : null;
}

export function isTruncatedUsbRow(row) {
  if (!row || typeof row !== 'object' || row.captureSource !== 'logcat') return false;
  if (row.truncated === true) return true;
  return Array.isArray(row.issues) && row.issues.some((i) => i && i.code === TRUNCATED_ISSUE_CODE);
}

/** present | empty | absent | unknown_truncated | unreadable - see the proxy twin. */
export function tokenStateFor({ a3p, truncatedUsb = false }, buyer) {
  const classes = (buyer && buyer.adapterClasses) || [];
  const base = { buyer: buyer ? buyer.id : null, adapterClass: classes[0] || null, entryFound: false, tokenPresent: false };
  if (!a3p) {
    return { ...base, state: truncatedUsb ? 'unknown_truncated' : 'absent', ...(truncatedUsb ? { tokenPresent: null } : {}) };
  }
  const walk = walkA3p(a3p);
  if (!walk.ok) return { ...base, tokenPresent: null, state: 'unreadable' };
  const mine = entriesForClasses(walk.entries, classes);
  if (!mine.length) return { ...base, state: 'absent' };
  const present = mine.some((e) => e.tokenPresent);
  return {
    ...base,
    adapterClass: mine[0].adapterClass,
    entryFound: true,
    tokenPresent: present,
    state: present ? 'present' : 'empty',
  };
}

export function rowTokenStates(row, buyers = TOKEN_BUYERS) {
  try {
    const a3p = a3pFromUrl(row && row.url);
    const truncatedUsb = isTruncatedUsbRow(row);
    return buyers.map((b) => tokenStateFor({ a3p, truncatedUsb }, b));
  } catch {
    return [];
  }
}
